import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Heart, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { currency } from "../lib/format";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState(null);
    const [polls, setPolls] = useState(0);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!sessionId) { setFailed(true); return; }
        let cancelled = false;
        let attempts = 0;

        async function poll() {
            attempts += 1;
            setPolls(attempts);
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (cancelled) return;
                setStatus(data);
                if (data.payment_status === "paid") return;
                if (attempts >= 10) { setFailed(true); return; }
                setTimeout(poll, 2000);
            } catch (e) {
                if (attempts >= 3) { setFailed(true); return; }
                setTimeout(poll, 2000);
            }
        }
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    const paid = status?.payment_status === "paid";

    return (
        <div className="max-w-[700px] mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
            {!paid && !failed && (
                <div>
                    <Loader2 className="w-10 h-10 text-[var(--rl-primary)] mx-auto animate-spin" />
                    <h1 className="font-serif text-3xl mt-6">Confirming your donation…</h1>
                    <p className="text-[var(--rl-text-2)] mt-2 text-sm">Checking Stripe (attempt {polls}/10). Please don't close this tab.</p>
                </div>
            )}
            {paid && (
                <div data-testid="payment-success-confirmed">
                    <div className="w-16 h-16 rounded-full bg-[var(--rl-primary)] text-white flex items-center justify-center mx-auto">
                        <Heart className="w-8 h-8" fill="white" strokeWidth={0} />
                    </div>
                    <h1 className="font-serif text-4xl mt-6">Thank you, {(status.donor_name || "friend").split(" ")[0]}.</h1>
                    <p className="text-[var(--rl-text-2)] mt-3 max-w-md mx-auto">Your gift of <strong>{currency(status.amount)}</strong> is on its way to the RescueLink Global Fund. It will fund medical care, transport, and boarding for at-risk dogs — worldwide.</p>
                    <div className="mt-8 flex flex-wrap gap-3 justify-center">
                        <Link to="/at-risk"><Button className="btn-primary rounded-full h-11 px-5">See dogs you helped</Button></Link>
                        <Link to="/community"><Button variant="outline" className="rounded-full h-11 px-5">Share the news</Button></Link>
                    </div>
                </div>
            )}
            {failed && (
                <div data-testid="payment-success-failed">
                    <h1 className="font-serif text-3xl">We couldn't confirm the payment.</h1>
                    <p className="text-[var(--rl-text-2)] mt-2">If your card was charged, we'll record the donation via webhook shortly. Otherwise please try again.</p>
                    <div className="mt-6 flex gap-3 justify-center">
                        <Link to="/donate"><Button className="btn-primary rounded-full h-11 px-5">Try again</Button></Link>
                        <Link to="/"><Button variant="outline" className="rounded-full h-11 px-5">Back home</Button></Link>
                    </div>
                </div>
            )}
        </div>
    );
}
